import React, { createContext, useContext, useState, useEffect } from 'react';
import { ProjectService, formatProjectFromApi } from '../services/ProjectService';
import { useAuth } from './AuthContext';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [methodology, setMethodology] = useState('scrum');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const { isAuthenticated } = useAuth();

  // Cargar proyectos desde la API
  const loadProjects = async () => {
    if (!isAuthenticated()) {
      console.log('Usuario no autenticado, no se pueden cargar proyectos');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const result = await ProjectService.getProjects();
      
      if (result.success) {
        // Formatear proyectos de la API al formato del frontend
        const formattedProjects = result.projects.map(formatProjectFromApi);
        setProjects(formattedProjects);
        
        // Guardar en localStorage para cache
        localStorage.setItem('projects', JSON.stringify(formattedProjects));
      } else {
        setError(result.error);
        console.error('Error loading projects:', result.error);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading projects:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Cargar proyectos desde localStorage primero (para mostrar algo inmediatamente)
    const savedProjects = localStorage.getItem('projects');
    if (savedProjects) {
      try {
        setProjects(JSON.parse(savedProjects));
      } catch (error) {
        console.error('Error parsing saved projects:', error);
        localStorage.removeItem('projects');
      }
    }
    
    // Luego cargar desde la API si el usuario está autenticado
    if (isAuthenticated()) {
      loadProjects();
    }
  }, [isAuthenticated]);

  // Crear un nuevo proyecto
  const createProject = async (projectData) => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('📤 Enviando datos del proyecto:', projectData);
      const result = await ProjectService.createProject(projectData);
      console.log('📥 Resultado del servicio:', result);
      
      if (result.success && result.project) {
        const formattedProject = formatProjectFromApi(result.project);
        
        if (!formattedProject) {
          console.error('❌ No se pudo formatear el proyecto');
          throw new Error('Error al formatear el proyecto creado');
        }
        
        console.log('✅ Proyecto formateado:', formattedProject);
        const updatedProjects = [...projects, formattedProject];
        setProjects(updatedProjects);
        localStorage.setItem('projects', JSON.stringify(updatedProjects));
        return { success: true, project: formattedProject };
      } else {
        const error = result.error || 'Error al crear el proyecto';
        console.error('❌ Error en el resultado:', error);
        setError(error);
        throw new Error(error);
      }
    } catch (err) {
      const errorMessage = err.message || 'Error al crear el proyecto';
      console.error('❌ Excepción al crear proyecto:', err);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Actualizar un proyecto
  const updateProject = async (projectId, updates) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await ProjectService.updateProject(projectId, updates);
      
      if (result.success) {
        const formattedProject = formatProjectFromApi(result.project);
        const updatedProjects = projects.map(project =>
          project.id === projectId ? formattedProject : project
        );
        setProjects(updatedProjects);
        localStorage.setItem('projects', JSON.stringify(updatedProjects));
        return formattedProject;
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Eliminar un proyecto
  const deleteProject = async (projectId) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await ProjectService.deleteProject(projectId);
      
      if (result.success) {
        const updatedProjects = projects.filter(project => project.id !== projectId);
        setProjects(updatedProjects);
        localStorage.setItem('projects', JSON.stringify(updatedProjects));
        
        if (currentProject?.id === projectId) {
          setCurrentProject(null);
        }
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Obtener un proyecto específico
  const getProject = async (projectId) => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await ProjectService.getProject(projectId);
      
      if (result.success) {
        return formatProjectFromApi(result.project);
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Recargar proyectos desde la API
  const refreshProjects = () => {
    loadProjects();
  };

  // Funciones para tareas (por ahora mantener compatibilidad local)
  const addTask = (projectId, task) => {
    const newTask = {
      id: Date.now().toString(),
      ...task,
      createdAt: new Date().toISOString()
    };
    
    const updatedProjects = projects.map(project =>
      project.id === projectId
        ? { ...project, tasks: [...project.tasks, newTask] }
        : project
    );
    setProjects(updatedProjects);
    localStorage.setItem('projects', JSON.stringify(updatedProjects));
    return newTask;
  };

  const updateTask = (projectId, taskId, updates) => {
    const updatedProjects = projects.map(project =>
      project.id === projectId
        ? {
            ...project,
            tasks: project.tasks.map(task =>
              task.id === taskId ? { ...task, ...updates } : task
            )
          }
        : project
    );
    setProjects(updatedProjects);
    localStorage.setItem('projects', JSON.stringify(updatedProjects));
  };

  const deleteTask = (projectId, taskId) => {
    const updatedProjects = projects.map(project =>
      project.id === projectId
        ? { ...project, tasks: project.tasks.filter(task => task.id !== taskId) }
        : project
    );
    setProjects(updatedProjects);
    localStorage.setItem('projects', JSON.stringify(updatedProjects));
  };

  const value = {
    projects,
    currentProject,
    methodology,
    loading,
    error,
    setCurrentProject,
    setMethodology,
    createProject,
    updateProject,
    deleteProject,
    getProject,
    refreshProjects,
    addTask,
    updateTask,
    deleteTask
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};
